/**
 * MRT overlays — transparent surfaces that must not rewrite the G-buffer
 * (2026-09-17: "when I enable AO the decals are just square black blocks
 * from footprints, and a light rectangle is around rain when it falls").
 *
 * THE CONTRACT. The scene pass carries several render targets (`output` +
 * `normal` + `velocity` + `metalness` + `roughness`, see
 * PostProcessingPipeline) and three r185 blends EACH attachment by the pass
 * MRT node's per-target blend mode: `WebGPUPipelineUtils.createRenderPipeline`
 * reads `mrt.getBlendMode(texture.name)` off the RENDER CONTEXT's MRT (the
 * pass node — `RenderContexts.get(renderTarget, renderer.getMRT())`), and
 * `MRTNode.blendModes` defaults every target but `output` to NoBlending. So a
 * transparent overlay — a footprint decal, a rain streak, a cloud sprite, a
 * haze shell, a nameplate — REPLACED the normal (and velocity / metalness /
 * roughness) of the ground behind it while its `depthWrite:false` left that
 * ground's depth in place. GTAO then shaded the ground under a decal with the
 * DECAL's normal: the left print is a mirrored InstancedMesh slot (scale −1)
 * and three's front-face rule is per OBJECT (`matrixWorld.determinantAffine()`),
 * so the slot rasterises back-facing and `faceDirection` flips its normal to
 * point DOWN — every horizon sample sits "above" the surface, full occlusion,
 * a black square that outlives the print (an expired ring-buffer slot still
 * draws at opacity 0 and still writes its normal). The rain quad's normal is
 * the EMITTER's, sideways to the ground: AO reads an unoccluded tilted surface
 * at the ground's depth — the light rectangle. SSR and the motion blur read
 * the same wrong targets.
 *
 * THE FIX has two halves, both on the PASS side:
 *  1. `installMrtOverlayBlending(passMrt, names)` — the pass MRT's surface
 *     targets take MaterialBlending, so a TRANSPARENT material blends them
 *     with its own blend function and with that target's OWN output alpha
 *     (WebGPU's src-alpha factor is per attachment). An opaque material has
 *     no blend state, so it still replaces every target; a transparent Normal
 *     material still replaces too (three pads a vec3 normal to alpha 1).
 *  2. `mrtSurfaceOutput(node)` wraps each surface output of the pass layout
 *     in a TSL `Fn` that reads the MATERIAL BEING BUILT (`builder.material`):
 *     a marked material writes vec4(0) with ALPHA 0 — Normal (Sa, 1−Sa),
 *     premultiplied (1, 1−Sa) and additive (Sa|1, 1) blending all leave the
 *     destination untouched (`overlayBlend` is the CPU mirror the test pins);
 *     every other material writes the node it always wrote. The branch is a
 *     compile-time JS decision, one shader per material as before.
 *
 * WHY NOT `material.mrtNode`: three consults the material's own MRT node when
 * the renderer has NONE — a reflector render, an RTT, a readback target — and
 * an MRT node whose outputs match no texture of a single unnamed target emits
 * an EMPTY output struct (the WGSL builder keys the struct return on
 * `isOutputStructNode`), an invalid pipeline, and a poisoned command buffer.
 * The pass-side branch never touches those renders.
 *
 * THE MARK is `markMrtOverlay(material)`: `userData.mrtOverlay = true` plus a
 * top-level node property `mrtOverlayNode` — `NodeMaterial._getNodeChildren`
 * walks the material's OWN node-valued properties into `customProgramCacheKey`
 * (userData is not walked; two materials with the same graph and a different
 * mark must not share a program), and `NodeLibrary.fromMaterial` copies every
 * enumerable key of a CLASSIC material onto its node twin, so a
 * `SpriteMaterial` / `MeshBasicMaterial` carries the mark as well.
 * WebGL2 keeps its single-output strip (no MRT there); WebGPU compatibility
 * mode applies the material blend to every target, which the alpha-0 write
 * satisfies too.
 *
 * LAW: a transparent material drawn OVER a surface it does not replace
 * (decals, particles, sprites, plates, haze, glow, beams) calls
 * `markMrtOverlay`. A transparent SURFACE (water, glass) does not — its own
 * normal is what the post passes should see.
 */
import * as THREE from 'three/webgpu';
import * as TSL from 'three/tsl';

/** The engine's unit tests stub `three/tsl` and `three/webgpu` per file; a stub without an export must not break the mark or the module. */
function tsl<T>(f: () => T, fallback: T): T { try { return f(); } catch { return fallback; } }

/** The pass targets an overlay must leave to the surface behind it. */
export const MRT_OVERLAY_TARGETS = ['normal', 'velocity', 'metalness', 'roughness'] as const;
export type MrtOverlayTarget = (typeof MRT_OVERLAY_TARGETS)[number];

/** three r185 exports MaterialBlending (6); the pinned @types/three ^0.182 does not. Read under a guard: the unit tests stub `three/webgpu` per file too. */
export const MATERIAL_BLENDING: number = tsl(() => (THREE as unknown as { MaterialBlending?: number }).MaterialBlending ?? 6, 6);

/** The subset of three's MRTNode / BlendMode surface this file touches (absent from the pinned types). */
interface MrtNodeLike {
  outputNodes?: Record<string, unknown>;
  blendModes?: Record<string, { blending: number }>;
  setBlendMode?: (name: string, blend: { blending: number }) => unknown;
  getBlendMode?: (name: string) => { blending: number };
}

function materialBlendMode(): { blending: number } {
  const Ctor = tsl(() => (THREE as unknown as { BlendMode?: new (blending: number) => { blending: number } }).BlendMode, undefined);
  return Ctor ? new Ctor(MATERIAL_BLENDING) : { blending: MATERIAL_BLENDING };
}

/**
 * Give the scene pass's surface targets the MATERIAL blend mode. Call it on
 * the node handed to `pass.setMRT(...)`, with the layout's target names
 * (`output` is skipped — three already blends it by the material). Returns
 * the names it applied, for the log line.
 */
export function installMrtOverlayBlending(passMrt: object, names: readonly string[]): string[] {
  const node = passMrt as MrtNodeLike;
  const applied: string[] = [];
  for (const name of names) {
    if (name === 'output') continue;
    if (node.outputNodes && !(name in node.outputNodes)) continue;
    if (typeof node.setBlendMode === 'function') node.setBlendMode(name, materialBlendMode());
    else if (node.blendModes) node.blendModes[name] = materialBlendMode();
    else continue;
    applied.push(name);
  }
  return applied;
}

/** True when the pass MRT blends `name` by the material (what `installMrtOverlayBlending` sets). */
export function mrtTargetBlendsByMaterial(passMrt: object, name: string): boolean {
  const node = passMrt as MrtNodeLike;
  const mode = node.getBlendMode ? node.getBlendMode(name) : node.blendModes?.[name];
  return mode?.blending === MATERIAL_BLENDING;
}

type Marked = { userData: Record<string, unknown>; mrtOverlayNode?: unknown };

/**
 * Mark a material as an overlay: the pass's surface outputs write alpha-0
 * zeros for it, so the surface behind it keeps its normal / velocity /
 * metalness / roughness. Idempotent. Works on node materials AND classic
 * ones (the mark rides `NodeLibrary.fromMaterial`'s key copy onto the twin).
 */
export function markMrtOverlay<M extends THREE.Material>(material: M): M {
  const m = material as M & Marked;
  const ud = (m.userData ??= {}); // a stubbed material may carry none
  if (ud.mrtOverlay === true) return material;
  // Probe lever: `globalThis.__disableMrtOverlay = true` before the materials
  // build reproduces the bug for a before/after picture (the motion-blur
  // probe's `__disableStaticMotionVectors` pattern).
  if ((globalThis as { __disableMrtOverlay?: boolean }).__disableMrtOverlay === true) return material;
  ud.mrtOverlay = true;
  // A top-level NODE property: it enters NodeMaterial.customProgramCacheKey
  // (userData does not) and is copied onto a classic material's node twin.
  m.mrtOverlayNode = tsl(() => TSL.float(1), undefined);
  return material;
}

/** True when `markMrtOverlay` has marked the material (or the node twin of a marked classic material). */
export function isMrtOverlay(material: unknown): boolean {
  const m = material as Partial<Marked> | null | undefined;
  return !!m && (m.userData?.mrtOverlay === true || m.mrtOverlayNode !== undefined);
}

/**
 * The pass-side decision, pure: what a surface output writes for `material`.
 * A marked material writes vec4(0) (alpha 0 — the blend leaves the target
 * alone); every other material writes `node`, exactly as before.
 */
export function surfaceOutputFor<N>(material: unknown, node: N): N | THREE.Node {
  return isMrtOverlay(material) ? tsl<N | THREE.Node>(() => TSL.vec4(0, 0, 0, 0), node) : node;
}

/**
 * Wrap one surface output of the pass MRT layout (`transformedNormalView`,
 * `velocity`, `metalness`, `roughness`). The returned node branches on the
 * material being built — a JS decision at shader-build time, one program per
 * material as before (the mark is in the cache key). Falls back to the bare
 * node when TSL is mocked away (the pipeline tests stub `three/tsl`).
 */
export function mrtSurfaceOutput<N>(node: N): N {
  const body = (builder: { material?: unknown }) => surfaceOutputFor(builder.material, node);
  return tsl(() => (TSL.Fn(body as never) as unknown as () => N)(), node);
}

export type OverlayBlendKind = 'none' | 'normal' | 'premultiplied' | 'additive' | 'additivePremultiplied';

/**
 * CPU mirror of the per-attachment blend equation the fix relies on:
 * `result = src·srcFactor + dst·dstFactor` with three's factors for the
 * material blend kinds. For an alpha-0 source every transparent kind returns
 * `dst` unchanged; `none` (NoBlending — three's old default for the surface
 * targets) returns `src` and is the bug.
 */
export function overlayBlend(
  src: readonly [number, number, number, number],
  dst: readonly [number, number, number, number],
  kind: OverlayBlendKind,
): [number, number, number, number] {
  const sa = src[3];
  let sf: number; let df: number;
  switch (kind) {
    case 'none': return [src[0], src[1], src[2], src[3]];
    case 'normal': sf = sa; df = 1 - sa; break;               // (SrcAlpha, OneMinusSrcAlpha)
    case 'premultiplied': sf = 1; df = 1 - sa; break;         // (One, OneMinusSrcAlpha)
    case 'additive': sf = sa; df = 1; break;                  // (SrcAlpha, One)
    case 'additivePremultiplied': sf = 1; df = 1; break;      // (One, One)
  }
  return [src[0] * sf + dst[0] * df, src[1] * sf + dst[1] * df, src[2] * sf + dst[2] * df, src[3] * sf + dst[3] * df];
}
